// ============================================================
//  src/admin/components/AdminLayout.js
// ============================================================
import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import './AdminLayout.css';
import { ADMIN_BASE } from '../adminConfig';

// Map raw DB role values to readable labels
const ROLE_LABELS = {
  '0': 'Super Admin',
  '1': 'Administrator',
  '2': 'Manager',
  '3': 'Support',
};

const TOP_NAV = [
  { icon: 'fa-chart-pie',    label: 'Dashboard',      to: `${ADMIN_BASE}/dashboard` },
  { icon: 'fa-users',        label: 'Subscribers',    to: `${ADMIN_BASE}/subscribers` },
  { icon: 'fa-receipt',      label: 'Transactions',   to: `${ADMIN_BASE}/transactions` },
  { icon: 'fa-headset',      label: 'Support',        to: `${ADMIN_BASE}/support` },
  { icon: 'fa-bell',         label: 'Notifications',  to: `${ADMIN_BASE}/notifications` },
  { icon: 'fa-wallet',       label: 'Credit/Debit',   to: `${ADMIN_BASE}/credit-debit` },
  { icon: 'fa-chart-column', label: 'Sales Analysis', to: `${ADMIN_BASE}/sales-analysis` },
  { icon: 'fa-user-shield',  label: 'System Users',   to: `${ADMIN_BASE}/system-users` },
];

const BOTTOM_NAV = [
  { icon: 'fa-circle-user',  label: 'My Account',     to: `${ADMIN_BASE}/account` },
  { icon: 'fa-layer-group',  label: 'Services',       to: `${ADMIN_BASE}/services` },
  { icon: 'fa-plug',         label: 'API Settings',   to: `${ADMIN_BASE}/api-settings` },
  { icon: 'fa-sliders',      label: 'Other Settings', to: `${ADMIN_BASE}/other-settings` },
  { icon: 'fa-chart-simple', label: 'User Stats',     to: `${ADMIN_BASE}/user-stats` },
  { icon: 'fa-gear',         label: 'Settings',       to: `${ADMIN_BASE}/settings` },
  // superOnly: true — only rendered for role === '0' (Super Admin)
  { icon: 'fa-rotate', label: 'Update',   to: `${ADMIN_BASE}/update` },
];

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const siteName  = localStorage.getItem('adminSiteName') || 'Admin';
  const roleLabel = ROLE_LABELS[String(admin?.role)] ?? 'Administrator';
  const isSuperAdmin = String(admin?.role) === '0';

  const handleLogout = async () => {
    await logout();
    navigate(`${ADMIN_BASE}/login`);
  };

  return (
    <div className="adm-shell">
      <aside className={`adm-sidebar ${sidebarOpen ? 'adm-sidebar--open' : ''}`}>
        <div className="adm-sidebar-brand">
          <span className="adm-brand-icon"><i className="fas fa-shield-halved" /></span>
          <span className="adm-brand-text">{siteName}</span>
        </div>

        <nav className="adm-nav">
          {TOP_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `adm-nav-item ${isActive ? 'adm-nav-item--active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <i className={`fas ${item.icon} adm-nav-icon`} />
              <span>{item.label}</span>
            </NavLink>
          ))}

          {BOTTOM_NAV.filter((item) => !item.superOnly || isSuperAdmin).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `adm-nav-item ${isActive ? 'adm-nav-item--active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <i className={`fas ${item.icon} adm-nav-icon`} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="adm-sidebar-footer">
          <div className="adm-admin-info">
            <div className="adm-admin-avatar">
              {(admin?.name?.[0] || 'A').toUpperCase()}
            </div>
            <div>
              <p className="adm-admin-name">{admin?.name || 'Admin'}</p>
              <p className="adm-admin-role">{roleLabel}</p>
            </div>
          </div>
          <button className="adm-logout-btn" onClick={handleLogout}>
            <i className="fas fa-right-from-bracket" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <div
        className={`adm-overlay ${sidebarOpen ? 'adm-overlay--visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      <div className="adm-main">
        <header className="adm-topbar">
          <button
            className="adm-burger"
            onClick={() => setSidebarOpen((p) => !p)}
            aria-label="Toggle menu"
          >
            <i className="fas fa-bars" />
          </button>
          <h1 className="adm-topbar-title">{siteName} Admin</h1>
          <div className="adm-topbar-right">
            <span className="adm-topbar-badge">
              <i className="fas fa-circle" style={{ color: '#00C48C', fontSize: 8 }} />
              &nbsp;Live
            </span>
          </div>
        </header>

        <div className="adm-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
